import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { clientPortalService } from '../../../services/clientPortalService';
import { notificationService } from '../../../services/notificationService';

const RecommendationsEngine = ({ clientId, onRefresh, onViewAll, fullView = false, totalCount = 0 }) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadRecommendations();

    // Subscribe to real-time updates
    const channel = notificationService?.subscribeToRecommendations(
      (newRec) => {
        // New recommendation inserted
        setRecommendations((prev) => [convertRecommendation(newRec), ...prev]);
      },
      (updatedRec) => {
        // Recommendation updated (e.g., status changed to approved)
        setRecommendations((prev) =>
          prev?.map((rec) => (rec?.id === updatedRec?.id ? convertRecommendation(updatedRec) : rec))
        );
      },
      (deletedRec) => {
        // Recommendation deleted
        setRecommendations((prev) => prev?.filter((rec) => rec?.id !== deletedRec?.id));
      }
    );

    return () => {
      notificationService?.unsubscribe(channel);
    };
  }, [clientId]);

  const convertRecommendation = (rec) => ({
    id: rec?.id,
    clientId: rec?.client_id,
    serviceName: rec?.service_name,
    category: rec?.category,
    rationale: rec?.rationale,
    predictedROI: rec?.predicted_roi,
    roiConfidence: rec?.roi_confidence,
    implementationMonths: rec?.implementation_months,
    costRange: rec?.cost_range,
    successMetrics: rec?.success_metrics,
    riskFactors: rec?.risk_factors,
    status: rec?.status,
    reviewedBy: rec?.reviewed_by,
    reviewedAt: rec?.reviewed_at,
    reviewNotes: rec?.review_notes,
    aiAnalysis: rec?.ai_analysis,
    createdAt: rec?.created_at,
    updatedAt: rec?.updated_at
  });

  const loadRecommendations = async () => {
    try {
      const data = await clientPortalService?.getRecommendations(clientId);
      setRecommendations(data?.recommendations || []);
    } catch (error) {
      console.error('Error loading recommendations:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const [updatingId, setUpdatingId] = useState(null);

  const getCategoryColor = (category) => {
    const colors = {
      'Governance': 'bg-purple-50 text-purple-700 border-purple-200',
      'Analytics': 'bg-blue-50 text-blue-700 border-blue-200',
      'Infrastructure': 'bg-teal-50 text-teal-700 border-teal-200',
      'Strategy': 'bg-[#E6F0F5] text-[#002B45] border-[#002B45]/20',
      'Skills': 'bg-orange-50 text-orange-700 border-orange-200'
    };
    return colors?.[category] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const getPriorityBadge = (priority) => {
    if (priority === 1) return { label: 'High Priority', color: 'bg-red-100 text-red-800' };
    if (priority === 2) return { label: 'Medium Priority', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'Low Priority', color: 'bg-green-100 text-green-800' };
  };

  const handleStatusUpdate = async (recommendationId, newStatus) => {
    try {
      setUpdatingId(recommendationId);
      await clientPortalService?.updateRecommendationStatus(recommendationId, newStatus);
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error updating recommendation status:', error);
      alert('Failed to update recommendation status');
    } finally {
      setUpdatingId(null);
    }
  };

  if (!recommendations || recommendations?.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-elevation-1 p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-[#002B45]">Personalized Recommendations</h3>
        </div>
        <div className="text-center py-12">
          <Icon name="Lightbulb" size={48} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No recommendations available</p>
          <p className="text-sm text-gray-400 mt-2">Complete an assessment to receive personalized recommendations</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-elevation-1 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center">
            <Icon name="Lightbulb" size={20} className="text-yellow-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-[#002B45]">Personalized Recommendations</h3>
            <p className="text-sm text-gray-500">{totalCount} active recommendations</p>
          </div>
        </div>
        {!fullView && onViewAll && (
          <Button
            onClick={onViewAll}
            variant="outline"
            size="sm"
            iconName="ArrowRight"
            iconPosition="right"
          >
            View All
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {recommendations?.map((recommendation) => {
          const priorityBadge = getPriorityBadge(recommendation?.priority);

          return (
            <div
              key={recommendation?.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-300"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${getCategoryColor(recommendation?.category)}`}>
                      {recommendation?.category}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${priorityBadge?.color}`}>
                      {priorityBadge?.label}
                    </span>
                  </div>
                  <h4 className="font-semibold text-[#002B45] mb-2">{recommendation?.title}</h4>
                  <p className="text-sm text-gray-600 mb-3">{recommendation?.description}</p>
                </div>
              </div>

              {recommendation?.estimatedImpact && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                  <p className="text-xs font-semibold text-green-900 mb-1 flex items-center gap-1">
                    <Icon name="TrendingUp" size={14} />
                    Estimated Impact
                  </p>
                  <p className="text-sm text-green-700">{recommendation?.estimatedImpact}</p>
                </div>
              )}

              {recommendation?.recommendedActions?.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Recommended Actions:</p>
                  <ul className="space-y-2">
                    {recommendation?.recommendedActions?.map((action, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <Icon name="CheckCircle" size={16} className="text-[#1FB5A5] mt-0.5 flex-shrink-0" />
                        <span>{action?.action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {recommendation?.resources?.length > 0 && fullView && (
                <div className="mb-3 pt-3 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Resources:</p>
                  <div className="flex flex-wrap gap-2">
                    {recommendation?.resources?.map((resource, idx) => (
                      <a
                        key={idx}
                        href={resource?.url}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs hover:bg-blue-100 transition-colors"
                      >
                        <Icon name="ExternalLink" size={12} />
                        {resource?.title}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {recommendation?.status === 'active' && (
                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <Button
                    onClick={() => handleStatusUpdate(recommendation?.id, 'completed')}
                    disabled={updatingId === recommendation?.id}
                    size="sm"
                    iconName="CheckCheck"
                    iconPosition="left"
                  >
                    {updatingId === recommendation?.id ? 'Updating...' : 'Mark Complete'}
                  </Button>
                  <Button
                    onClick={() => handleStatusUpdate(recommendation?.id, 'dismissed')}
                    disabled={updatingId === recommendation?.id}
                    variant="outline"
                    size="sm"
                  >
                    Dismiss
                  </Button>
                </div>
              )}

              {recommendation?.status === 'completed' && (
                <div className="pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-green-600">
                    <Icon name="CheckCircle" size={16} />
                    <span className="text-sm font-medium">Completed</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RecommendationsEngine;